import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  TimePicker
} from "antd";
import dayjs from "dayjs";
import { createSprZal, fetchSprZal, fetchSprZalWarehouses, saveSprZal } from "./spr-zal-api";

const STEP_OPTIONS = [
  { value: 5, label: "5 мин" },
  { value: 10, label: "10 мин" },
  { value: 15, label: "15 мин" },
  { value: 30, label: "30 мин" },
  { value: 60, label: "1 час" }
];

const LABEL_WIDTH = 170;

const formItemLayout = {
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto" },
  labelAlign: "right"
};

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toTime = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const timeToIso = (value) => {
  if (!value || !value.isValid()) return null;
  // Дата не важна для PLAN_TIME_*; сохраняем время суток.
  return value.second(0).millisecond(0).format("YYYY-MM-DDTHH:mm:ss");
};

const normalizeCard = (row) => ({
  idZal: toNumber(pick(row, "idZal", "IdZal")),
  idUpper: toNumber(pick(row, "idUpper", "IdUpper")),
  isGroup: Number(pick(row, "isGroup", "IsGroup")) === 1,
  name: pick(row, "name", "Name") ?? "",
  planStepMin: toNumber(pick(row, "planStepMin", "PlanStepMin")) ?? 5,
  planTimeBeg: pick(row, "planTimeBeg", "PlanTimeBeg"),
  planTimeEnd: pick(row, "planTimeEnd", "PlanTimeEnd"),
  edChild: toNumber(pick(row, "edChild", "EdChild")),
  edParent: toNumber(pick(row, "edParent", "EdParent")),
  idSkl: toNumber(pick(row, "idSkl", "IdSkl")),
  childCount: toNumber(pick(row, "childCount", "ChildCount")) ?? 0
});

const cardToFormValues = (card) => ({
  idZal: card.idZal,
  idUpper: card.idUpper == null || card.idUpper === 0 ? -1 : card.idUpper,
  isGroup: Boolean(card.isGroup),
  name: card.name,
  planStepMin: card.planStepMin ?? 5,
  planTimeBeg: toTime(card.planTimeBeg),
  planTimeEnd: toTime(card.planTimeEnd),
  edChild: card.edChild,
  edParent: card.edParent,
  idSkl: card.idSkl && card.idSkl > 0 ? card.idSkl : undefined
});

const emptyCard = (idUpper) => ({
  idZal: null,
  idUpper: idUpper == null || idUpper === 0 ? -1 : idUpper,
  isGroup: false,
  name: "",
  planStepMin: 5,
  planTimeBeg: null,
  planTimeEnd: null,
  edChild: null,
  edParent: null,
  idSkl: null,
  childCount: 0
});

/**
 * Вкладка «Основная» (поля как Delphi TFrmEZalSan + признак Группа).
 */
const SprZalMain = ({
  idZal,
  isNew = false,
  idUpper,
  open,
  parentOptions = [],
  onSaved,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [childCount, setChildCount] = useState(0);
  const [warehouses, setWarehouses] = useState([]);

  const groupLocked = !isNew && childCount > 0;

  const warehouseOptions = useMemo(
    () => [
      { value: 0, label: "—" },
      ...(warehouses || []).map((w) => ({
        value: toNumber(pick(w, "id", "Id")),
        label: pick(w, "name", "Name") ?? ""
      })).filter((o) => o.value != null)
    ],
    [warehouses]
  );

  const parentSelectOptions = useMemo(
    () => [
      { value: -1, label: "— корень —" },
      ...(parentOptions || [])
        .filter((o) => o.value !== idZal)
        .map((o) => ({ value: o.value, label: o.label }))
    ],
    [parentOptions, idZal]
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchSprZalWarehouses()
      .then((data) => {
        if (!cancelled) setWarehouses(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setWarehouses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    if (isNew) {
      const card = emptyCard(idUpper);
      setChildCount(0);
      setError("");
      setLoading(false);
      form.setFieldsValue(cardToFormValues(card));
      return () => {
        cancelled = true;
      };
    }

    if (idZal == null) return undefined;

    setLoading(true);
    setError("");
    fetchSprZal(idZal)
      .then((row) => {
        if (cancelled) return;
        const card = normalizeCard(row);
        setChildCount(card.childCount);
        form.setFieldsValue(cardToFormValues(card));
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить кабинет");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, idZal, idUpper, form]);

  const onFinish = async (values) => {
    setSaving(true);
    setError("");
    const body = {
      idUpper: values.idUpper == null || values.idUpper === 0 ? -1 : values.idUpper,
      isGroup: groupLocked || values.isGroup ? 1 : 0,
      name: (values.name || "").trim(),
      planStepMin: values.planStepMin ?? 5,
      planTimeBeg: timeToIso(values.planTimeBeg),
      planTimeEnd: timeToIso(values.planTimeEnd),
      edChild: values.edChild ?? null,
      edParent: values.edParent ?? null,
      idSkl: values.idSkl && values.idSkl > 0 ? values.idSkl : null
    };

    try {
      if (isNew) {
        const created = await createSprZal(body);
        onSaved?.(created);
      } else {
        const saved = await saveSprZal(idZal, body);
        setChildCount(toNumber(pick(saved, "childCount", "ChildCount")) ?? childCount);
        onSaved?.(saved);
      }
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="spr-zal-card__center">
        <Spin tip="Загрузка..." />
      </div>
    );
  }

  return (
    <div className="spr-zal-card__tab">
      <div className="spr-zal-card__body">
        {error ? <Alert className="spr-zal-card__error" type="error" showIcon message={error} /> : null}
        <Form
          form={form}
          className="spr-zal-card__form"
          {...formItemLayout}
          colon={false}
          onFinish={onFinish}
          disabled={saving}
        >
          {!isNew ? (
            <Form.Item label="Код" name="idZal">
              <Input disabled />
            </Form.Item>
          ) : null}
          <Form.Item
            label="Наименование"
            name="name"
            rules={[{ required: true, message: "Укажите наименование" }]}
          >
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="Родитель" name="idUpper">
            <Select options={parentSelectOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item
            label="Группа"
            name="isGroup"
            valuePropName="checked"
            extra={groupLocked ? "Нельзя снять: есть вложенные элементы" : undefined}
          >
            <Checkbox disabled={groupLocked} />
          </Form.Item>
          <Form.Item label="Шаг времени в мин." name="planStepMin">
            <Select options={STEP_OPTIONS} />
          </Form.Item>
          <Form.Item label="Начало работы" name="planTimeBeg">
            <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
          </Form.Item>
          <Form.Item label="Окончание работы" name="planTimeEnd">
            <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
          </Form.Item>
          <Form.Item label="Процед. ед. дети" name="edChild">
            <InputNumber style={{ width: "100%" }} step={0.1} />
          </Form.Item>
          <Form.Item label="Процед. ед. взрослые" name="edParent">
            <InputNumber style={{ width: "100%" }} step={0.1} />
          </Form.Item>
          <Form.Item label="Склад" name="idSkl">
            <Select allowClear options={warehouseOptions} showSearch optionFilterProp="label" />
          </Form.Item>
        </Form>
      </div>
      <div className="spr-zal-card__footer">
        <Button
          type="primary"
          className="spr-zal-card__ok"
          loading={saving}
          onClick={() => form.submit()}
        >
          ОК
        </Button>
        <Button onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
    </div>
  );
};

export default SprZalMain;
